/**
 * executeRedo - Executes redo operations
 *
 * Maps redo operation data to API calls that reapply mutations.
 */

import type {
	RedoOperationData,
	CreateBlockRedoData,
	UpdateBlockRedoData,
	DeleteBlockRedoData,
	CreateReferenceRedoData,
	DeleteReferenceRedoData,
	MoveBlockRedoData,
	ReorderBlocksRedoData,
	ApplyFormationRedoData,
	RemoveFormationRedoData,
	ApplyConceptRedoData,
} from './Types'
import { secureFetch, secureJsonFetch } from '@/utils/SecureFetch'

/**
 * Executes the redo operation to reapply a mutation.
 * @param redoData - Data describing how to redo the operation
 * @throws Error if the API call fails
 */
export async function executeRedo(redoData: RedoOperationData): Promise<void> {
	switch (redoData.type) {
		case 'create_block':
			await executeCreateBlockRedo(redoData)
			break
		case 'update_block':
			await executeUpdateBlockRedo(redoData)
			break
		case 'delete_block':
			await executeDeleteBlockRedo(redoData)
			break
		case 'create_reference':
			await executeCreateReferenceRedo(redoData)
			break
		case 'delete_reference':
			await executeDeleteReferenceRedo(redoData)
			break
		case 'move_block':
			await executeMoveBlockRedo(redoData)
			break
		case 'reorder_blocks':
			await executeReorderBlocksRedo(redoData)
			break
		case 'apply_formation':
			await executeApplyFormationRedo(redoData)
			break
		case 'remove_formation':
			await executeRemoveFormationRedo(redoData)
			break
		case 'apply_concept':
			await executeApplyConceptRedo(redoData)
			break
		default: {
			// TypeScript exhaustiveness check
			const _exhaustive: never = redoData
			throw new Error(`Unknown redo operation type: ${(_exhaustive as RedoOperationData).type}`)
		}
	}
}

/**
 * Redo create_block: Recreate the block with its children and references.
 */
async function executeCreateBlockRedo(data: CreateBlockRedoData): Promise<void> {
	// First, recreate the parent block
	const response = await secureJsonFetch('/api/blocks', {
		method: 'POST',
		body: data.block,
	})

	if (!response.ok) {
		throw new Error(`Failed to recreate block: ${response.statusText}`)
	}

	// Then recreate children in order
	for (const child of data.children) {
		const childResponse = await secureJsonFetch('/api/blocks', {
			method: 'POST',
			body: child,
		})

		if (!childResponse.ok) {
			throw new Error(`Failed to recreate child block: ${childResponse.statusText}`)
		}
	}

	// Finally, recreate references
	for (const ref of data.references) {
		const refResponse = await secureJsonFetch('/api/references', {
			method: 'POST',
			body: {
				source_block_id: ref.source_block_id,
				target_block_id: ref.target_block_id,
				reference_type: ref.reference_type,
				display_options: ref.display_options,
				display_order: ref.display_order,
			},
		})

		if (!refResponse.ok) {
			throw new Error(`Failed to recreate reference: ${refResponse.statusText}`)
		}
	}
}

/**
 * Redo update_block: Apply new values.
 */
async function executeUpdateBlockRedo(data: UpdateBlockRedoData): Promise<void> {
	const response = await secureJsonFetch(`/api/blocks/${data.blockId}/update`, {
		method: 'PATCH',
		body: data.newValues,
	})

	if (!response.ok) {
		throw new Error(`Failed to update block ${data.blockId}: ${response.statusText}`)
	}
}

/**
 * Redo delete_block: Delete the block.
 */
async function executeDeleteBlockRedo(data: DeleteBlockRedoData): Promise<void> {
	const response = await secureFetch(`/api/blocks/${data.blockId}/delete`, {
		method: 'DELETE',
	})

	if (!response.ok) {
		throw new Error(`Failed to delete block ${data.blockId}: ${response.statusText}`)
	}
}

/**
 * Redo create_reference: Create the reference.
 */
async function executeCreateReferenceRedo(data: CreateReferenceRedoData): Promise<void> {
	const response = await secureJsonFetch('/api/references', {
		method: 'POST',
		body: {
			source_block_id: data.sourceBlockId,
			target_block_id: data.targetBlockId,
			reference_type: data.referenceType,
			display_options: data.displayOptions,
			display_order: data.displayOrder,
		},
	})

	if (!response.ok) {
		throw new Error(`Failed to create reference: ${response.statusText}`)
	}
}

/**
 * Redo delete_reference: Delete the reference.
 */
async function executeDeleteReferenceRedo(data: DeleteReferenceRedoData): Promise<void> {
	const response = await secureFetch(`/api/references/${data.referenceId}`, {
		method: 'DELETE',
	})

	if (!response.ok) {
		throw new Error(`Failed to delete reference ${data.referenceId}: ${response.statusText}`)
	}
}

/**
 * Redo move_block: Move block to new position.
 */
async function executeMoveBlockRedo(data: MoveBlockRedoData): Promise<void> {
	const response = await secureJsonFetch(`/api/blocks/${data.blockId}/move`, {
		method: 'PATCH',
		body: {
			parent_block_id: data.newParentBlockId,
			display_order: data.newDisplayOrder,
		},
	})

	if (!response.ok) {
		throw new Error(`Failed to move block ${data.blockId}: ${response.statusText}`)
	}
}

/**
 * Redo reorder_blocks: Apply new display orders.
 */
async function executeReorderBlocksRedo(data: ReorderBlocksRedoData): Promise<void> {
	// Convert Map to array of updates
	const updates = Array.from(data.newOrders.entries()).map(([blockId, displayOrder]) => ({
		block_id: blockId,
		display_order: displayOrder,
	}))

	const response = await secureJsonFetch('/api/blocks/reorder', {
		method: 'PATCH',
		body: {
			parent_block_id: data.parentBlockId,
			updates,
		},
	})

	if (!response.ok) {
		throw new Error(`Failed to reorder blocks: ${response.statusText}`)
	}
}

/**
 * Redo apply_formation: Apply formation to play.
 */
async function executeApplyFormationRedo(data: ApplyFormationRedoData): Promise<void> {
	const response = await secureJsonFetch(`/api/plays/${data.playBlockId}/formation-ref`, {
		method: 'POST',
		body: { formation_block_id: data.formationBlockId },
	})

	if (!response.ok) {
		throw new Error(`Failed to apply formation: ${response.statusText}`)
	}
}

/**
 * Redo remove_formation: Remove formation from play.
 */
async function executeRemoveFormationRedo(data: RemoveFormationRedoData): Promise<void> {
	const response = await secureFetch(`/api/plays/${data.playBlockId}/formation-ref`, {
		method: 'DELETE',
	})

	// 404 is OK - means no formation to remove
	if (!response.ok && response.status !== 404) {
		throw new Error(`Failed to remove formation: ${response.statusText}`)
	}
}

/**
 * Redo apply_concept: Apply concept to play.
 */
async function executeApplyConceptRedo(data: ApplyConceptRedoData): Promise<void> {
	const response = await secureJsonFetch(`/api/plays/${data.playBlockId}/concept-refs`, {
		method: 'POST',
		body: { concept_block_id: data.conceptBlockId },
	})

	if (!response.ok) {
		throw new Error(`Failed to apply concept: ${response.statusText}`)
	}
}
